import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import MyDialogContent from "@/components/MyDialogContent";
import { formatDateTime12 } from "@/utils/helpers";
import clsx from "clsx";
import { Download, Headset, MessageSquareText, Pause, Phone, Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ConflictRow, TranscriptionUtterance } from "../types";
import {
  formatCallCardPhone,
  getTranscriptParticipantLabels,
  isCareTeamUtterance,
  isGraceBotCall,
  resolveTranscriptUtteranceLabel,
} from "../utils";
import { ConflictCallTypeBadge } from "./ConflictCallTypeBadge";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conflict?: ConflictRow | null;
  transcript?: string;
  utterances?: TranscriptionUtterance[];
};

function formatUtteranceTime(seconds?: number) {
  if (seconds == null || !Number.isFinite(seconds)) return "";
  const total = Math.max(0, Math.floor(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatDuration(conflict?: ConflictRow | null) {
  if (!conflict) return null;
  if (conflict.duration) return String(conflict.duration);
  const seconds = Number(conflict.durationSeconds);
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

export default function TranscriptDialog({
  open,
  onOpenChange,
  conflict,
  transcript,
  utterances = [],
}: Props) {
  const hasUtterances = utterances.length > 0;
  const phone = formatCallCardPhone(conflict);
  const participantLabels = getTranscriptParticipantLabels(conflict);
  const isGraceCall = isGraceBotCall(conflict);
  const callType = isGraceCall ? "inbound" : conflict?.callType;
  const callAt = conflict?.callAt || conflict?.createdAt;
  const recordingUrl = conflict?.recordingUrl || conflict?.attachmentUrl;
  const recordingName =
    conflict?.recordingName || conflict?.attachmentName || "recording.mp3";
  const durationLabel = formatDuration(conflict);
  const messageCount = utterances.filter((u) =>
    String(u.text ?? "").trim(),
  ).length;
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  const fallbackDuration = Number(conflict?.durationSeconds);
  const totalDuration =
    Number.isFinite(audioDuration) && audioDuration > 0
      ? audioDuration
      : Number.isFinite(fallbackDuration) && fallbackDuration > 0
        ? fallbackDuration
        : 0;
  const progressPercent =
    totalDuration > 0 ? Math.min(100, (currentTime / totalDuration) * 100) : 0;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
    setAudioDuration(0);
  }, [open, recordingUrl]);

  const onAudioTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTime(audio.currentTime || 0);
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      setAudioDuration(audio.duration);
    }
  };

  const seekRecording = (value: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(value)) return;
    audio.currentTime = value;
    setCurrentTime(value);
  };

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || !recordingUrl) return;
    try {
      if (audio.paused) {
        await audio.play();
        setIsPlaying(true);
      } else {
        audio.pause();
        setIsPlaying(false);
      }
    } catch {
      setIsPlaying(false);
    }
  };

  const downloadRecording = async () => {
    if (!recordingUrl) return;
    setIsDownloading(true);
    try {
      const response = await fetch(recordingUrl);

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = recordingName || "recording.mp3";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download recording:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MyDialogContent
        heading="Call Transcript"
        className="!h-auto max-h-[90vh] w-[96dvw] sm:!max-w-3xl"
        bodyClassName="!overflow-hidden !flex !flex-col !p-4 !pt-0"
        headerComponent={
          <p className="text-sm text-muted-foreground teachers mt-1">
            {/* Conversation between the caller and your {isGraceCall ? "Grace Agent" : "care team"}. */}
          </p>
        }
      >
        {conflict ? (
          <div className="shrink-0 rounded-lg border border-grey bg-grey-light/40 px-4 py-3 space-y-2 mb-4">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-[#111827] teachers">
                Call • {callAt ? formatDateTime12(callAt) : "—"} • {phone}
              </p>
              {callType ? (
                <ConflictCallTypeBadge value={callType} />
              ) : null}
            </div>
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground teachers">
              {durationLabel ? <span>Duration {durationLabel}</span> : null}
              {durationLabel && messageCount > 0 ? <span>|</span> : null}
              {messageCount > 0 ? (
                <span>
                  {messageCount} message{messageCount === 1 ? "" : "s"}
                </span>
              ) : null}
              {(durationLabel || messageCount > 0) && recordingUrl ? (
                <span>|</span>
              ) : null}
              {recordingUrl ? (
                <span className="inline-flex min-w-0 items-center gap-1">
                  <audio
                    ref={audioRef}
                    src={recordingUrl}
                    preload="metadata"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                    onTimeUpdate={onAudioTimeUpdate}
                    onLoadedMetadata={onAudioTimeUpdate}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0 text-primary"
                    onClick={() => void togglePlayback()}
                    aria-label={isPlaying ? "Pause recording" : "Play recording"}
                    title={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <Pause className="h-3 w-3" />
                    ) : (
                      <Play className="h-3 w-3" />
                    )}
                  </Button>
                  <span className="shrink-0 text-[10px] tabular-nums">
                    {formatUtteranceTime(currentTime)}
                  </span>
                  <input
                    type="range"
                    min={0}
                    max={totalDuration || 0}
                    step={0.1}
                    value={Math.min(currentTime, totalDuration || 0)}
                    disabled={!totalDuration}
                    onChange={(event) => seekRecording(Number(event.target.value))}
                    className="h-1 w-16 shrink-0 cursor-pointer accent-primary sm:w-24"
                    style={{
                      background: `linear-gradient(to right, var(--primary) ${progressPercent}%, #E5E7EB ${progressPercent}%)`,
                    }}
                    aria-label="Recording progress"
                  />
                  <span className="shrink-0 text-[10px] tabular-nums">
                    {formatUtteranceTime(totalDuration)}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0 text-primary"
                    onClick={() => void downloadRecording()}
                    disabled={isDownloading}
                    aria-label="Download recording"
                    title="Download"
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </span>
              ) : null}
            </div>
          </div>
        ) : null}

        {hasUtterances ? (
          <div className="min-h-0 flex-1 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className="rounded-full border-grey bg-white px-3 py-1 teachers text-xs font-medium text-tertiary"
              >
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-secondary" />
                {participantLabels.externalLabel}
              </Badge>
              <Badge
                variant="outline"
                className="rounded-full border-grey bg-white px-3 py-1 teachers text-xs font-medium text-tertiary"
              >
                <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-primary" />
                {participantLabels.internalLabel}
              </Badge>
            </div>

            <div className="min-h-[280px] max-h-[min(58vh,480px)] overflow-y-auto rounded-xl border border-grey bg-white p-4">
              <div className="space-y-4">
                {utterances.map((utterance, index) => {
                  const isInternal = isCareTeamUtterance(utterance);
                  const text = String(utterance.text ?? "").trim();
                  if (!text) return null;

                  return (
                    <div
                      key={`${utterance.startTime ?? index}-${index}`}
                      className={clsx(
                        "flex w-full items-end gap-2.5",
                        isInternal ? "flex-row-reverse" : "flex-row",
                      )}
                    >
                      <Avatar className="h-8 w-8 shrink-0">
                        <AvatarFallback
                          className={clsx(
                            "teachers text-xs font-semibold",
                            isInternal
                              ? "bg-[#DBEAFE] text-[#1D4ED8]"
                              : "bg-[#E8F5EF] text-[#2F855A]",
                          )}
                        >
                          {isInternal ? (
                            <Headset className="h-3.5 w-3.5" />
                          ) : (
                            <Phone className="h-3.5 w-3.5" />
                          )}
                        </AvatarFallback>
                      </Avatar>

                      <div
                        className={clsx(
                          "max-w-[78%] min-w-0",
                          isInternal ? "items-end" : "items-start",
                          "flex flex-col",
                        )}
                      >
                        <div
                          className={clsx(
                            "mb-1 flex items-center gap-2 px-1 text-[11px] font-medium text-muted-foreground teachers",
                            isInternal && "flex-row-reverse",
                          )}
                        >
                          <span>{resolveTranscriptUtteranceLabel(utterance, conflict)}</span>
                          {utterance.startTime != null ? (
                            <span className="tabular-nums">
                              {formatUtteranceTime(utterance.startTime)}
                            </span>
                          ) : null}
                        </div>

                        <div
                          className={clsx(
                            "rounded-2xl px-3.5 py-2.5 shadow-sm",
                            isInternal
                              ? "rounded-br-md border border-primary/20 bg-primary/[0.08] text-[#111827]"
                              : "rounded-bl-md border border-grey bg-grey-light/60 text-[#111827]",
                          )}
                        >
                          <p className="text-sm leading-relaxed teachers whitespace-pre-wrap">
                            {text}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-grey bg-white p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-[#111827] teachers">
              <MessageSquareText className="h-4 w-4 text-primary" />
              Transcript
            </div>
            <p className="whitespace-pre-wrap text-sm text-tertiary teachers leading-relaxed">
              {transcript?.trim() || "No transcript available for this call."}
            </p>
          </div>
        )}
      </MyDialogContent>
    </Dialog>
  );
}
